import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetDashboard } from "@/hooks/dashboard";
import { useGetAllDashboards, useUpdateDashboard, useMoveWidget } from "@/hooks";
import { IDashboard, VisualizationType } from "@/types/dashboard.types";
import { addWidgetToLayout, removeWidgetFromLayout, fromRGLLayouts, toRGLLayouts } from "@/utils/layout-update";
import { COLS } from "@/components/dashboard/dashboard-grid";
import { Layouts } from "react-grid-layout";
import { Loader2, Move } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface MoveWidgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  widgetId: string;
  currentDashboardId: string;
  widgetVisualizationType: VisualizationType;
  onMoveSuccess: () => void;
}

export function MoveWidgetModal({
  isOpen,
  onClose,
  widgetId,
  currentDashboardId,
  widgetVisualizationType,
  onMoveSuccess,
}: MoveWidgetModalProps) {
  const [selectedDashboardId, setSelectedDashboardId] = useState<string>("");
  const [isMoving, setIsMoving] = useState(false);

  const { data: dashboardsResponse, isLoading } = useGetAllDashboards();
  const updateDashboardMutation = useUpdateDashboard();
  const moveWidgetMutation = useMoveWidget();

  const { data: targetDashboardData } = useGetDashboard(selectedDashboardId);
  const { data: sourceDashboardData } = useGetDashboard(currentDashboardId);

  const dashboards = dashboardsResponse?.data || [];

  // Filter out the current dashboard
  const availableDashboards = dashboards.filter(
    (dashboard: IDashboard) => dashboard.id !== currentDashboardId
  );

  const handleMove = async () => {
    if (!selectedDashboardId) {
      toast.error("Please select a dashboard");
      return;
    }

    setIsMoving(true);
    try {
      const targetDashboard = targetDashboardData?.data;
      const sourceDashboard = sourceDashboardData?.data;

      if (!targetDashboard) {
        toast.error("Failed to load target dashboard");
        return;
      }

      // Move the widget via API
      const moveResult = await moveWidgetMutation.mutateAsync({
        id: widgetId,
        targetDashboardId: selectedDashboardId,
      });

      if (moveResult.success) {
        // Update target dashboard layout to include the moved widget
        if (targetDashboard.layout) {
          const currentLayouts: Layouts = toRGLLayouts(targetDashboard.layout.layouts);
          const updatedLayouts: Layouts = addWidgetToLayout(
            currentLayouts,
            widgetId,
            COLS,
            widgetVisualizationType
          );

          const targetLayoutPayload = {
            formData: {
              layout: {
                layouts: fromRGLLayouts(updatedLayouts),
                order: [
                  ...targetDashboard.layout.order.map((id: any) => id.toString()),
                  widgetId,
                ],
              },
            },
            id: selectedDashboardId,
          };

          await updateDashboardMutation.mutateAsync(targetLayoutPayload);
        }

        // Update source dashboard layout to remove the widget
        if (sourceDashboard?.layout) {
          const currentLayouts: Layouts = toRGLLayouts(sourceDashboard.layout.layouts);
          const updatedLayouts: Layouts = removeWidgetFromLayout(currentLayouts, widgetId);

          const sourceLayoutPayload = {
            formData: {
              layout: {
                layouts: fromRGLLayouts(updatedLayouts),
                order: sourceDashboard.layout.order.filter((id: any) => id.toString() !== widgetId),
              },
            },
            id: currentDashboardId,
          };

          await updateDashboardMutation.mutateAsync(sourceLayoutPayload);
        }

        toast.success("Widget moved successfully");
        onMoveSuccess();
        onClose();
        setSelectedDashboardId("");
      } else {
        toast.error(moveResult.message || "Failed to move widget");
      }
    } catch (error) {
      console.error("Failed to move widget:", error);
      toast.error("Failed to move widget");
    } finally {
      setIsMoving(false);
    }
  };

  const handleClose = () => {
    setSelectedDashboardId("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Move className="w-5 h-5" />
            Move Widget
          </DialogTitle>
          <DialogDescription>
            Select a dashboard to move this widget to. The widget will be removed from the current dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Target Dashboard</label>
            <Select
              value={selectedDashboardId}
              onValueChange={setSelectedDashboardId}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a dashboard" />
              </SelectTrigger>
              <SelectContent>
                {availableDashboards.map((dashboard: IDashboard) => (
                  <SelectItem key={dashboard.id} value={dashboard.id}>
                    {dashboard.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {availableDashboards.length === 0 && !isLoading && (
              <p className="text-sm text-gray-500 mt-2">
                No other dashboards available
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isMoving}>
            Cancel
          </Button>
          <Button
            onClick={handleMove}
            disabled={!selectedDashboardId || isMoving || isLoading}
          >
            {isMoving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Moving...
              </>
            ) : (
              <>
                <Move className="w-4 h-4 mr-2" />
                Move Widget
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
